import { GREEN_LOAN_LIMIT } from "./data";
import type {
	GreenLoanCalcStep,
	GreenLoanSmallCalculator1Response,
	GreenLoanSmallCalculator2Response,
	GreenLoanSmallCalculator3Response,
	GreenLoanSmallCalculatorRequest,
	GreenLoanSmallCalculatorResponse,
} from "./types";

function calcStep1(income: number): number {
	// 1.
	// Megadott jövedelem összege kisebb-e, mint 500 ezer Ft.

	// Ha kisebb, akkor vegyük az 50 százalékát
	// Ha nem kisebb, akkor vegyük a 60 százalékát
	return income < 500_000 ? income * 0.5 : income * 0.6;
}

function calcStep3(income: number, loanAmountNeed: number): GreenLoanCalcStep {
	// 3.
	// A fennmaradó jövedelmi rész mennyi kamatmentes kölcsönösszegre lenne elég?
	// Szorozzuk fel 300-val a fennmaradó jövedelmi összeget.
	let interestFreeLoanAmount = income * 300;

	// Az igy kapott összeget maximalizáljuk le a 2,5 millió forinttal csökkentett kért hitelösszeg fényében.
	if (interestFreeLoanAmount > loanAmountNeed) {
		interestFreeLoanAmount = loanAmountNeed;
	}

	// EREDMÉNY 2
	// A kamatmentes összeg maximuma nem éri el a még fennálló igényelt összeg nagyságát, akkor véget ért a folyamat.

	return { income, loanAmount: interestFreeLoanAmount, end: interestFreeLoanAmount < loanAmountNeed };
}

function calcStep4(
	interestFreeLoanAmount: number,
	csokLoanAmount: number,
	csokLoanInstallment: number,
	loanAmountNeed: number,
): GreenLoanCalcStep {
	let end = false;
	let installment = 0;
	// 4.
	// A fentebb maximalizált kért összegből vonjunk le 15 millió Ft-ot
	const newInterestFreeLoanAmount = interestFreeLoanAmount - csokLoanAmount;

	// 4.a
	// Ha nincs 15 millió Ft a korrrigált kért összeg, akkor a kalkuláció véget ért
	if (loanAmountNeed < csokLoanAmount) {
		end = true;
		// A korrigált összeget osszuk el 300-val, az a törlesztőrészlet.
		installment = Math.round(newInterestFreeLoanAmount / 300);
		// EREDMÉNY 3
	}

	// 4.b
	// Ha van még maradék összeg, akkor vonjunk le a még elkölthető törlesztőből 50 ezer forintot
	if (!end && newInterestFreeLoanAmount > 0) {
		installment -= csokLoanInstallment;
	}

	return { loanAmount: newInterestFreeLoanAmount, end, installment };
}

function calcStep5(currentLoanAmount: number): number {
	// 5.
	// Nézzük meg mennyi lenne a maradék igényelt összeg törlesztőrészlete
	// Vegyük a maradék kért összeg 0,45%-át.
	let greenLoanInstallment = currentLoanAmount;
	greenLoanInstallment *= 0.0045;
	greenLoanInstallment = Math.round(greenLoanInstallment);

	return greenLoanInstallment;
}

function calcGreenLoanEntitled(
	mode: "calculate1And2" | "calculate3",
	greenLoanInstallment: number,
	income: number,
	csokLoanInstallment?: number,
	loanAmountSum?: number,
	maximalizedLoanAmount?: number,
): boolean {
	if (mode === "calculate3") {
		return greenLoanInstallment > 0 && income >= greenLoanInstallment;
	}

	return (
		greenLoanInstallment > 0 &&
		income >= greenLoanInstallment + csokLoanInstallment &&
		loanAmountSum >= maximalizedLoanAmount
	);
}

function calculate1And2(
	maximalizedLoanAmount: number,
	request: GreenLoanSmallCalculatorRequest,
	csokLoanAmountMax: number,
	installmentModifer: number,
): {
	greenLoanEntitled: boolean;
	greenLoanInstallment: number;
	loanAmount: number;
	loanAmountSum: number;
	csokLoanEntitled: boolean;
	csokLoanAmount: number;
	csokLoanInstallment: number;
} {
	const csokLoanAmount = maximalizedLoanAmount < csokLoanAmountMax ? maximalizedLoanAmount : csokLoanAmountMax;
	const csokLoanInstallment =
		maximalizedLoanAmount < csokLoanAmountMax ? Math.round(maximalizedLoanAmount / 300) : installmentModifer;
	let { income } = request;
	let greenLoanInstallment = 0;
	income = calcStep1(income);

	// Még szükséges hitelösszeg
	const step3Response = calcStep3(income, maximalizedLoanAmount);
	let modifiedLoanAmount = step3Response.loanAmount;

	if (!step3Response?.end) {
		const step4Response = calcStep4(modifiedLoanAmount, csokLoanAmount, csokLoanInstallment, maximalizedLoanAmount);

		modifiedLoanAmount = step4Response.loanAmount;
		greenLoanInstallment = step4Response.installment;

		if (!step4Response?.end) {
			greenLoanInstallment = calcStep5(modifiedLoanAmount);
		}
	}

	const loanAmountSum = csokLoanAmount + modifiedLoanAmount;
	const greenLoanEntitled = calcGreenLoanEntitled(
		"calculate1And2",
		greenLoanInstallment,
		income,
		csokLoanInstallment,
		loanAmountSum,
		maximalizedLoanAmount,
	);

	return {
		csokLoanInstallment,
		greenLoanInstallment,
		loanAmount: modifiedLoanAmount,
		csokLoanAmount,
		loanAmountSum,
		greenLoanEntitled,

		csokLoanEntitled:
			csokLoanInstallment > 0 &&
			income >= csokLoanInstallment &&
			(greenLoanEntitled || csokLoanAmount === maximalizedLoanAmount),
	};
}

function calculate1(
	maximalizedLoanAmount: number,
	request: GreenLoanSmallCalculatorRequest,
	csokLoanAmountMax: number,
	installmentModifer: number,
): GreenLoanSmallCalculator1Response {
	const {
		greenLoanInstallment,
		loanAmount,
		loanAmountSum,
		greenLoanEntitled,
		csokLoanEntitled,
		csokLoanAmount,
		csokLoanInstallment,
	} = calculate1And2(maximalizedLoanAmount, request, csokLoanAmountMax, installmentModifer);

	return {
		calc1CsokEntitled: csokLoanEntitled,
		calc1CsokLoanAmount: csokLoanAmount,
		calc1CsokLoanInstallment: csokLoanInstallment,

		calc1GreenLoanEntitled: greenLoanEntitled,
		calc1GreenLoanLoanAmount: loanAmount,
		calc1GreenLoanInstallment: greenLoanInstallment,
		calc1GreenLoanLoanAmountSum: loanAmountSum,
		calc1GreenLoanInstallmentSum: csokLoanInstallment + greenLoanInstallment,
	};
}

function calculate2(
	maximalizedLoanAmount: number,
	request: GreenLoanSmallCalculatorRequest,
	csokLoanAmountMax: number,
	installmentModifer: number,
): GreenLoanSmallCalculator2Response {
	const {
		greenLoanInstallment,
		loanAmount,
		loanAmountSum,
		greenLoanEntitled,
		csokLoanEntitled,
		csokLoanAmount,
		csokLoanInstallment,
	} = calculate1And2(maximalizedLoanAmount, request, csokLoanAmountMax, installmentModifer);

	return {
		calc2CsokEntitled: csokLoanEntitled,
		calc2CsokLoanAmount: csokLoanAmount,
		calc2CsokLoanInstallment: csokLoanInstallment,

		calc2GreenLoanEntitled: greenLoanEntitled,
		calc2GreenLoanLoanAmount: loanAmount,
		calc2GreenLoanInstallment: greenLoanInstallment,
		calc2GreenLoanLoanAmountSum: loanAmountSum,
		calc2GreenLoanInstallmentSum: csokLoanInstallment + greenLoanInstallment,
	};
}

function calculate3(
	maximalizedLoanAmount: number,
	request: GreenLoanSmallCalculatorRequest,
): GreenLoanSmallCalculator3Response {
	let { income } = request;
	income = calcStep1(income);

	const greenLoanInstallment = calcStep5(maximalizedLoanAmount);

	return {
		calc3GreenLoanEntitled: calcGreenLoanEntitled("calculate3", greenLoanInstallment, income),
		calc3GreenLoanLoanAmount: maximalizedLoanAmount,
		calc3GreenLoanInstallment: greenLoanInstallment,
		calc3GreenLoanLoanAmountSum: maximalizedLoanAmount,
		calc3GreenLoanInstallmentSum: greenLoanInstallment,
	};
}

function getInstallments(
	calculate1Result: GreenLoanSmallCalculator1Response,
	calculate2Result: GreenLoanSmallCalculator2Response,
	calculate3Result: GreenLoanSmallCalculator3Response,
): number[] {
	const installments = [];

	if (calculate1Result.calc1GreenLoanEntitled || calculate1Result.calc1CsokEntitled) {
		installments.push(calculate1Result.calc1GreenLoanInstallmentSum);
	}

	if (calculate2Result.calc2GreenLoanEntitled || calculate2Result.calc2CsokEntitled) {
		installments.push(calculate2Result.calc2GreenLoanInstallmentSum);
	}

	if (calculate3Result.calc3GreenLoanEntitled) {
		installments.push(calculate3Result.calc3GreenLoanInstallmentSum);
	}

	return installments;
}

function getLoanAmounts(
	calculate1Result: GreenLoanSmallCalculator1Response,
	calculate2Result: GreenLoanSmallCalculator2Response,
	calculate3Result: GreenLoanSmallCalculator3Response,
): number[] {
	const loanAmounts = [];

	if (calculate1Result.calc1GreenLoanEntitled || calculate1Result.calc1CsokEntitled) {
		loanAmounts.push(calculate1Result.calc1GreenLoanLoanAmountSum);
	}

	if (calculate2Result.calc2GreenLoanEntitled || calculate2Result.calc2CsokEntitled) {
		loanAmounts.push(calculate2Result.calc2GreenLoanLoanAmountSum);
	}

	if (calculate3Result.calc3GreenLoanEntitled) {
		loanAmounts.push(calculate3Result.calc3GreenLoanLoanAmountSum);
	}

	return loanAmounts;
}

export function calculate(request: GreenLoanSmallCalculatorRequest): {
	errorMessage: string;
	response: GreenLoanSmallCalculatorResponse;
} {
	const { loanAmount } = request;
	// maximalizáljuk a loanAmountot a számításhoz
	const maximalizedLoanAmount = loanAmount > GREEN_LOAN_LIMIT ? GREEN_LOAN_LIMIT : loanAmount;
	const calculate1Result = calculate1(maximalizedLoanAmount, request, 15_000_000, 50_000);
	const calculate2Result = calculate2(maximalizedLoanAmount, request, 10_000_000, 33_334);
	const calculate3Result = calculate3(maximalizedLoanAmount, request);
	const installments = getInstallments(calculate1Result, calculate2Result, calculate3Result);
	const loanAmounts = getLoanAmounts(calculate1Result, calculate2Result, calculate3Result);

	const installmentMinAmount = Math.min(...installments);
	const installmentMaxAmount = Math.max(...installments);
	const maxloanAmount = Math.max(...loanAmounts);
	const errorMessage: string =
		!calculate1Result.calc1GreenLoanEntitled &&
		!calculate2Result.calc2GreenLoanEntitled &&
		!calculate3Result.calc3GreenLoanEntitled
			? "A jövedelmed nem birja el a hitelösszeghez kapcsolódó törlesztőrészletet"
			: null;

	return {
		errorMessage,
		response: {
			loanAmount: maximalizedLoanAmount,
			loanMax70mEntitled: loanAmount > GREEN_LOAN_LIMIT || maxloanAmount > GREEN_LOAN_LIMIT,
			necesseryLoadAmount: loanAmount > GREEN_LOAN_LIMIT ? loanAmount - GREEN_LOAN_LIMIT : 0,
			installmentMinAmount,
			installmentMaxAmount: installmentMinAmount === installmentMaxAmount ? 0 : installmentMaxAmount,
			...calculate1Result,
			...calculate2Result,
			...calculate3Result,
		},
	};
}
